<template>
  <v-dialog v-model="localVisible" max-width="500" persistent>
    <v-card>
      <v-card-title class="appeal-modal__header d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon icon="mdi-gavel" class="mr-2" />
          <span>Appeal Content Removal</span>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :disabled="submitting"
          @click="closeModal"
        />
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6">
        <!-- Success State -->
        <div v-if="successMessage" class="text-center py-4">
          <v-icon icon="mdi-check-circle" color="success" size="64" class="mb-4" />
          <p class="text-body-1 font-weight-medium">{{ successMessage }}</p>
          <p class="text-body-2 mt-2" style="opacity: 0.7">
            Our moderation team will review your appeal and you'll be notified of the outcome.
          </p>
        </div>

        <!-- Form State -->
        <v-form v-else ref="formRef" @submit.prevent="submitAppeal">
          <!-- Content Preview (if available) -->
          <div v-if="postTitle || postBody" class="appeal-modal__content-preview mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon icon="mdi-file-document" size="18" class="mr-2" />
              <span class="text-body-2 font-weight-medium">Removed {{ contentType === 'comment' ? 'Comment' : 'Post' }}</span>
            </div>
            <div class="appeal-modal__preview-box">
              <div v-if="postTitle" class="appeal-modal__title font-weight-medium mb-2">
                {{ postTitle }}
              </div>
              <div v-if="postBody" class="appeal-modal__body" v-html="postBody"></div>
            </div>
          </div>

          <!-- Violation Info (if available) -->
          <v-alert
            v-if="violationType"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            <template #prepend>
              <v-icon icon="mdi-alert-circle" />
            </template>
            <div class="text-body-2">
              <strong>Violation:</strong> {{ violationType }}
            </div>
          </v-alert>

          <p class="text-body-2 mb-4" style="opacity: 0.7">
            If you believe your content was removed in error, please explain why you think the decision should be reconsidered. Be specific and provide any relevant context.
          </p>

          <!-- Appeal Reason Textarea -->
          <v-textarea
            v-model="appealText"
            label="Appeal Explanation"
            :rules="appealRules"
            :error-messages="errorMessage"
            :disabled="submitting"
            variant="outlined"
            rows="5"
            counter
            maxlength="1000"
            placeholder="Explain why you believe this content should be restored..."
            auto-grow
          />

          <!-- Additional Context (optional) -->
          <v-expansion-panels variant="accordion" class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <v-icon icon="mdi-information" size="18" class="mr-2" />
                Additional Information (Optional)
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-textarea
                  v-model="additionalContext"
                  label="Additional Context"
                  :disabled="submitting"
                  variant="outlined"
                  rows="3"
                  counter
                  maxlength="500"
                  placeholder="Any additional context or evidence you'd like to provide..."
                  auto-grow
                  hide-details
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Appeal Guidelines -->
          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            class="mt-4"
          >
            <div class="text-body-2">
              <strong>Guidelines:</strong>
              <ul class="mt-1 pl-4">
                <li>Be respectful and factual in your explanation</li>
                <li>Include any relevant context that may have been misunderstood</li>
                <li>Appeals are typically reviewed within 24-48 hours</li>
              </ul>
            </div>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider v-if="!successMessage" />

      <v-card-actions v-if="!successMessage" class="pa-4">
        <v-spacer />
        <v-btn variant="text" :disabled="submitting" @click="closeModal">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="submitAppeal"
        >
          <v-icon icon="mdi-send" class="mr-1" />
          Submit Appeal
        </v-btn>
      </v-card-actions>

      <v-card-actions v-else class="pa-4">
        <v-spacer />
        <v-btn color="primary" variant="tonal" @click="closeModal">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useSdk } from '@/composables/useSdk';
import { resolveApiBaseUrl } from '@/utils/env';

interface Props {
  modelValue: boolean;
  /** The post ULID to appeal */
  postId: string;
  /** Optional title of the removed content */
  postTitle?: string;
  /** Optional body/text of the removed content */
  postBody?: string;
  /** Optional violation type that was cited */
  violationType?: string;
  /** Optional content type (post, comment, etc.) */
  contentType?: string;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'submitted', postId: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  postTitle: '',
  postBody: '',
  violationType: '',
  contentType: 'post',
});

const emit = defineEmits<Emits>();

const { getTokens } = useSdk();

// Local state
const localVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const formRef = ref<HTMLFormElement | null>(null);
const appealText = ref('');
const additionalContext = ref('');
const submitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

// Validation rules
const appealRules = [
  (v: string) => !!v?.trim() || 'Please provide an explanation for your appeal',
  (v: string) => (v?.trim().length ?? 0) >= 20 || 'Please provide at least 20 characters',
  (v: string) => (v?.trim().length ?? 0) <= 1000 || 'Maximum 1000 characters allowed',
];

// Computed
const canSubmit = computed(() => {
  if (submitting.value) return false;
  const text = appealText.value.trim();
  return text.length >= 20 && text.length <= 1000;
});

// Methods
function closeModal() {
  if (!submitting.value) {
    localVisible.value = false;
  }
}

function resetForm() {
  appealText.value = '';
  additionalContext.value = '';
  successMessage.value = '';
  errorMessage.value = '';
}

async function submitAppeal() {
  if (!canSubmit.value) return;

  errorMessage.value = '';
  submitting.value = true;

  try {
    // Combine appeal text with additional context if provided
    let fullAppealText = appealText.value.trim();
    if (additionalContext.value.trim()) {
      fullAppealText += '\n\n--- Additional Context ---\n' + additionalContext.value.trim();
    }

    // Get auth token for the request
    const tokens = getTokens();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (tokens?.accessToken) {
      headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }

    // Make the API call
    const apiBaseUrl = resolveApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/v1/users/me/violation/appeal`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        postId: props.postId,
        text: fullAppealText,
      }),
    });

    if (response.ok || response.status === 202) {
      successMessage.value = 'Your appeal has been submitted successfully.';
      emit('submitted', props.postId);
    } else {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to submit appeal');
    }
  } catch (error) {
    console.error('Failed to submit appeal:', error);
    errorMessage.value = error instanceof Error ? error.message : 'Failed to submit appeal. Please try again.';
  } finally {
    submitting.value = false;
  }
}

// Watch for dialog close to reset
watch(localVisible, (visible) => {
  if (!visible) {
    // Small delay to allow animation
    setTimeout(resetForm, 300);
  }
});
</script>

<style scoped lang="scss">
.appeal-modal__header {
  font-size: 1.1rem;
  font-weight: 600;
  padding-top: calc(16px + var(--safe-area-top));
}

.appeal-modal__content-preview {
  .appeal-modal__preview-box {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.7);
    border-left: 3px solid rgba(var(--v-theme-error), 0.5);
    max-height: 200px;
    overflow-y: auto;
  }

  .appeal-modal__title {
    font-size: 0.9rem;
    color: rgba(var(--v-theme-on-surface), 0.9);
  }

  .appeal-modal__body {
    white-space: pre-wrap;
    word-break: break-word;
  }
}

.v-expansion-panel {
  background: transparent !important;

  &::before {
    box-shadow: none !important;
  }
}

.v-expansion-panel-title {
  font-size: 0.875rem;
  min-height: 40px !important;
  padding: 8px 0 !important;
}

.v-expansion-panel-text__wrapper {
  padding: 8px 0 !important;
}
</style>
